const FALSE_AFTERPRINT_MS = 250;
const PRINT_RESTORE_FALLBACK_MS = 60_000;

function subscribeMediaChange(mediaQueryList, onChange) {
  if (mediaQueryList.addEventListener) {
    mediaQueryList.addEventListener("change", onChange);
    return () => mediaQueryList.removeEventListener("change", onChange);
  }
  if (mediaQueryList.addListener) {
    mediaQueryList.addListener(onChange);
    return () => mediaQueryList.removeListener(onChange);
  }
  return () => {};
}

/**
 * Opens the browser print dialog and resolves after print preview finishes.
 * Safari/Firefox often fire `afterprint` as soon as `print()` returns; ignore that
 * and wait for a later `afterprint`, `matchMedia('print')` leaving print, or fallback.
 */
export function openPricingReportPrintDialog({
  print = window.print.bind(window),
  matchMedia = window.matchMedia.bind(window),
  addEventListener = window.addEventListener.bind(window),
  removeEventListener = window.removeEventListener.bind(window),
} = {}) {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    let settled = false;
    let unsubscribeMedia = () => {};
    let fallbackTimer = 0;

    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      removeEventListener("afterprint", onAfterPrint);
      unsubscribeMedia();
      window.clearTimeout(fallbackTimer);
      resolve();
    };

    const onAfterPrint = () => {
      if (Date.now() - startedAt < FALSE_AFTERPRINT_MS) {
        return;
      }
      finish();
    };

    const mediaQueryList = matchMedia("print");
    unsubscribeMedia = subscribeMediaChange(mediaQueryList, (event) => {
      if (!event.matches) {
        finish();
      }
    });

    addEventListener("afterprint", onAfterPrint);
    fallbackTimer = window.setTimeout(finish, PRINT_RESTORE_FALLBACK_MS);
    if (typeof fallbackTimer?.unref === "function") {
      fallbackTimer.unref();
    }

    try {
      print();
    } catch (err) {
      if (!settled) {
        settled = true;
        removeEventListener("afterprint", onAfterPrint);
        unsubscribeMedia();
        window.clearTimeout(fallbackTimer);
      }
      reject(err);
    }
  });
}
